import * as React from 'react';
import { CurrencyCode } from '../../../types/currencyCode';
import {
	PerilUpdateRequest,
	PolicyCharacteristics,
} from '../../../types/policy';
import PerilLimitSelect from '../Perils/PerilLimitSelect';

interface Props {
	perilObj: any;
	id: string;
	perilConfig: any;
	isInEditMode: boolean;
	userHasWriteAccess?: boolean;
	policyCharacteristic: PolicyCharacteristics;
	updateLimit: (name: string, value: string) => void;
	currency: CurrencyCode;
}

interface PerilFormData {
	name: keyof PerilUpdateRequest;
	removeKey: keyof PerilUpdateRequest;
	label: string;
	value: string;
	setText: string;
	unsetText: string;
	unlimitedText?: string;
}

const UpdatedPerilDetails = (props: Props) => {
	const { perilObj, id, perilConfig, isInEditMode, updateLimit, currency } =
		props;

	// check if limit has been updated
	const iPIUpdated =
		perilObj.indemnityPerItem || perilObj.removeIndemnityPerItem;
	const iPEUpdated =
		perilObj.indemnityPerEvent || perilObj.removeIndemnityPerEvent;
	const iIAUpdated =
		perilObj.indemnityInAggregate || perilObj.removeIndemnityInAggregate;
	const lSUpdated = perilObj.lumpSumPayment || perilObj.removeLumpSumPayment;
	const dUpdated = perilObj.deductible || perilObj.removeDeductible;

	// determine if the value should be based off the update object or from the characteristic
	// if peril is a PerilCreateRequest, always use the perilObj
	const indemnityPerItem =
		iPIUpdated || perilObj.locator
			? perilObj.indemnityPerItem
			: perilConfig.indemnityPerItem;
	const indemnityPerEvent =
		iPEUpdated || perilObj.locator
			? perilObj.indemnityPerEvent
			: perilConfig.indemnityPerEvent;
	const indemnityInAggregate =
		iIAUpdated || perilObj.locator
			? perilObj.indemnityInAggregate
			: perilConfig.indemnityInAggregate;
	const lumpSumPayment =
		lSUpdated || perilObj.locator
			? perilObj.lumpSumPayment
			: perilConfig.lumpSumPayment;
	const deductible =
		dUpdated || perilObj.locator ? perilObj.deductible : perilConfig.deductible;

	const perilData: any = {
		indemnityPerItem: {
			label: 'Indemnity Per Item',
			name: 'indemnityPerItem',
			removeKey: 'removeIndemnityPerItem',
			value: indemnityPerItem,
			setText: 'Limited',
			unsetText: 'Unset',
			unlimitedText: 'Unlimited',
		},
		indemnityPerEvent: {
			label: 'Indemnity Per Event',
			name: 'indemnityPerEvent',
			removeKey: 'removeIndemnityPerEvent',
			value: indemnityPerEvent,
			setText: 'Limited',
			unsetText: 'Unset',
			unlimitedText: 'Unlimited',
		},
		indemnityInAggregate: {
			label: 'Indemnity In Aggregate',
			name: 'indemnityInAggregate',
			removeKey: 'removeIndemnityInAggregate',
			value: indemnityInAggregate,
			setText: 'Limited',
			unsetText: 'Unset',
			unlimitedText: 'Unlimited',
		},
		lumpSumPayment: {
			label: 'Lump Sum Payment',
			name: 'lumpSumPayment',
			removeKey: 'removeLumpSumPayment',
			value: lumpSumPayment,
			setText: 'Set',
			unsetText: 'Unset',
			unlimitedText: null as any,
		},
		deductible: {
			label: 'Deductible',
			name: 'deductible',
			removeKey: 'removeDeductible',
			value: deductible,
			setText: 'Set',
			unsetText: 'Unset',
			unlimitedText: null as any,
		},
	};

	const renderLimitRow = (perilData: PerilFormData) => {
		const { label, name, value, setText, unlimitedText, removeKey } = perilData;
		return (
			<PerilLimitSelect
				key={`${id}-${name}`}
				id={`${id}-${name}`}
				isInEditMode={isInEditMode}
				label={label}
				name={name}
				value={value}
				currency={currency}
				setText={setText}
				unsetText={'Unset'}
				unlimitedText={unlimitedText}
				removeKey={removeKey}
				userHasWriteAccess={props.userHasWriteAccess}
				updateLimit={updateLimit}
			/>
		);
	};

	const limitsChaged =
		iPIUpdated || iPEUpdated || iIAUpdated || lSUpdated || dUpdated;

	return limitsChaged ? (
		<div>
			<h4>Limits</h4>
			{iPIUpdated && renderLimitRow(perilData.indemnityPerItem)}
			{iPEUpdated && renderLimitRow(perilData.indemnityPerEvent)}
			{iIAUpdated && renderLimitRow(perilData.indemnityInAggregate)}
			{lSUpdated && renderLimitRow(perilData.lumpSumPayment)}
			{dUpdated && renderLimitRow(perilData.deductible)}
		</div>
	) : null;
};

export default UpdatedPerilDetails;
